// src/i18n.js
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: false,
    interpolation: { escapeValue: false },
    resources: {
      // ==========================================
      // --- ENGLISH ---
      // ==========================================
      en: {
        translation: {
          sidebar: {
            dashboard: 'Dashboard',
            hedger_strategies: 'Hedger Strategies',
            my_strategies: 'My Strategies',
            connected_exchange: 'Connected exchange',
            buy_usdt: 'Buy USDT',
            sell_usdt: 'Sell USDT',
            auto_trading: 'Auto-trading',
            pair_analysis: 'Search Strategies',
            admin_control: 'Admin Control',
            faq: 'FAQ',
            partner_program: 'Partner Program',
            sign_in: 'Sign In',
            sign_out: 'Sign Out'
          },
          stats: {
            title: 'Hedger Strategies',
            subtitle: 'Official algorithmic configurations for automated trading',
            by_hedger: 'By Hedger',
            risk_low: 'Low Risk',
            risk_medium: 'Medium Risk',
            risk_high: 'High Risk',
            open_btn: 'Open Strategy',
            preview_mode: 'Preview Mode',
            default_name: 'Strategy Name',
            default_desc: 'Official algorithmic configuration.',
            labels: {
              z_period: 'Z-Period',
              bars: 'Bars',
              margin: 'Margin',
              leverage: 'Leverage',
              comm: 'Comm.',
              z_exit: 'Z Exit',
              levels_plus: 'Levels (+)',
              levels_minus: 'Levels (-)'
            },
            errors: {
              title: 'Error loading catalog',
              access_denied: 'Access Denied: Please contact admin to make this route public.',
              generic: 'Failed to load strategies.',
              empty: 'No public strategies available yet.'
            }
          },
          equity: {
            title: 'Yield chart'
          },
          strategy_settings: {
            title: 'Strategy Settings',
            symbol_a: 'Symbol A',
            symbol_b: 'Symbol B',
            timeframe: 'Timeframe',
            zscore: 'Z-Score Period',
            bar_count: 'Bar Count',
            margin: 'Margin (USDT)',
            leverage: 'Leverage',
            position_size: 'Position Size (USDT)',
            commission: 'Commission Rate (%)',
            exit: 'Exit (Z-Score)',
            entry_plus: 'Entry Level (+)',
            entry_minus: 'Entry Level (-)',
            apply_btn: 'Apply & Run Simulation',
            info: 'Enter levels separated by commas.',
            date_filter: 'Exclude Dates',
            exclude_toggle: 'Enable date exclusion',
            from: 'From',
            to: 'To',
            min_size_error: "Minimum position size is $25. Increase Margin or Leverage.",
            rec_balance_title: "Recommended Futures Balance",
            rec_balance_desc: "To support {{count}} entry levels with a ${{size}} position size.",
            tooltips: {
              symbol: 'Select the coin to be traded.',
              timeframe: 'Select the chart for backtesting. 5m-30m recommended.',
              zscore: 'Distance between deviations. Default 300.',
              bar_count: 'Historical candles count. Max 50,000.',
              margin: 'The actual amount of USDT you risk from your balance.',
              leverage: 'Multiplier for your margin.',
              pos_size: 'Calculated total trade value (Margin x Leverage).',
              commission: 'Trade fee for opening/closing in %.',
              exit: 'Stop-loss deviation level.',
              entry_plus: 'Positive Z-Score entry triggers.',
              entry_minus: 'Negative Z-Score entry triggers.'
            }
          },
          autotrading: {
            page_title: 'Auto-trading',
            connect_title: 'Connect New Strategy',
            connected_title: 'Connected Strategies',
            select_account: '1. Select Account',
            select_strategies: '2. Select Strategies',
            placeholder_account: 'Choose an exchange account...',
            no_strategies: 'No available strategies to connect.',
            btn_connect: 'Enable auto trading',
            btn_connecting: 'Connecting...',
            empty_state: 'No active connections. Use the form above to connect strategies.',
            active_count: 'Active strategies',
            status: { disabled: 'Disabled', active: 'Trading Active', stopping: 'Stopping (Graceful)' },
            controls: { start: 'Start', stop_graceful: 'Stop (Graceful)', stop: 'Stop', force_stop: 'Force Stop', processing: 'Processing...' },
            modal: { params_title: 'Strategy Parameters', unlink_title: 'Unlink Strategy', unlink_msg: 'Are you sure you want to unlink strategy "{{name}}"?', btn_cancel: 'Cancel', btn_confirm: 'Confirm Unlink', settings: 'Settings', unlink: 'Unlink' },
            params: { symbol_a: 'Symbol A', symbol_b: 'Symbol B', timeframe: 'Timeframe', zscore: 'Z-Score Period', bar: 'Bar Analysis', amount: 'Entry Amount', commission: 'Commission', short: 'Short Levels', long: 'Long Levels' }
          },
          accounts: {
            page_title: 'Connected Exchanges',
            your_accounts: 'Your Accounts',
            loading: 'Loading accounts...',
            no_accounts: 'You have no connected accounts yet.',
            add_title: 'Add New Account',
            select_exchange: 'Select an exchange to connect:',
            coming_soon: 'Coming soon...',
            details_for: 'Enter details for {{exchange}}',
            verified: 'Verified',
            fields: { name: 'Account Name', name_placeholder: 'e.g., My Main Account', api_key: 'API Key', api_secret: 'API Secret', api_key_new: 'New API Key (optional)', api_secret_new: 'New API Secret (optional)' },
            btns: { connect: 'Connect Account', connecting: 'Connecting...', saving: 'Saving...', save_changes: 'Save Changes', delete: 'Delete', deleting: 'Deleting...', cancel: 'Cancel', update_keys: 'Update keys', delete_account: 'Delete account' },
            modal: { update_title: 'Update account: {{name}}', update_hint: 'Enter new values. Only filled fields will be updated.', delete_title: 'Confirm Deletion', delete_confirm: 'Are you sure you want to delete the account {{name}}?' },
            referral_modal: {
              title: 'Bybit Referral Check',
              header: 'Your exchange UID ({{uid}}) was not found',
              desc: 'Your account is not linked to our partner program. Access to automated trading is only available for our referrals.',
              btn_register: 'Register New Account',
              btn_transfer: 'How to Transfer UID',
              footer: 'After registration or transfer, try connecting again.'
            },
            bybit_help: {
              title: 'Bybit registration and referral transfer',
              desc: 'If you do not have a Bybit account yet, register using our partner link. If you already have an account, follow the guide to transfer the referral code.',
              btn_register: 'Register Bybit Account',
              btn_transfer: 'Referral Transfer Guide',
              hint: 'Only partner-linked Bybit accounts are supported for connection.'
            },
            errors: { network: 'A network error occurred.', unexpected: 'An unexpected error occurred.' }
          },
          auth: {
            common: {
              email_placeholder: 'Email address',
              password_placeholder: 'Password',
              show_password: 'Show password',
              hide_password: 'Hide password'
            },
            login: {
              subtitle: 'Welcome back, trader',
              forgot_password: 'Forgot your password?',
              sign_in: 'Sign in',
              signing_in: 'Signing in...',
              no_account: "Don't have an account yet?",
              register_here: 'Register here',
              footer: 'Secure access to your trading strategies and analytics',
              unexpected_error: 'An unexpected error occurred.'
            },
            register: {
              title: 'Create Account',
              subtitle: 'Join the Hedger',
              confirm_password_placeholder: 'Repeat password',
              have_referral: 'I have a referral code',
              referral_label: 'Referral code',
              referral_placeholder: 'Enter your referral code',
              referral_hint: 'If you came via a referral link, the code is already filled.',
              submit: 'Register',
              processing: 'Processing...',
              have_account: 'Already have an account?',
              sign_in: 'Sign In',
              footer: 'Create your account to access analytics and strategy tools',
              unexpected_error: 'Registration failed. Please try again.',
              errors: {
                passwords_mismatch: 'Passwords do not match.',
                referral_required: 'Please enter a referral code.',
                token_missing: 'Registration token not received. Please try again.'
              }
            }
          },
          hub: {
            page_title: 'My Strategies',
            sort_by: 'Sort by:',
            sort_name: 'Name',
            sort_date: 'Date',
            create_new: 'Create New Strategy',
            last_edited: 'Last edited:',
            invalid_strategy: 'Invalid Strategy',
            corrupted_data: 'Strategy data is corrupted.',
            untitled: 'Untitled Strategy',
            none: 'None',
            error_id: 'Error: Missing Strategy ID',
            params: { pair: 'Pair', timeframe: 'Timeframe', zscore: 'Z-Score Period', entry_plus: 'Entry Levels (+)', entry_minus: 'Entry Levels (-)' },
            modal: { confirm_title: 'Confirm Deletion', question: 'Delete Strategy?', message: 'Are you sure you want to delete "{{name}}"?', btn_cancel: 'Cancel', btn_delete: 'Delete Strategy', deleting: 'Deleting...', error_delete: 'Failed to delete' }
          },
          detail: {
            page_title: 'Strategy Simulation',
            new_strategy: 'Creating new strategy',
            error_title: 'Error Loading Strategy',
            error_invalid_id: 'Invalid Strategy ID.',
            error_not_found: 'Strategy not found.',
            back_btn: 'Back to Strategies',
            placeholder: 'Configure settings on the right and press "Apply & Run Simulation".',
            open_trades: 'Open Trade Entries',
            history: 'Completed Trades History',
            orderbook: {
              title: 'Bybit Orderbooks',
              subtitle: 'Live snapshots for the strategy symbols with spread, visible depth and an estimated entry size that should pass through the book more cleanly.',
              source: 'Bybit V5 REST · /v5/market/orderbook',
              refresh_rate: 'Refresh every {{seconds}}s',
              refreshing: 'Refreshing snapshots...',
              updated: 'Updated {{time}}',
              no_symbols: 'Symbols are not configured yet, so there is nothing to request from Bybit.',
              unavailable: 'Unavailable',
              error: 'Failed to load Bybit orderbook.',
              snapshot_hint: 'Top {{depth}} levels per side',
              best_bid: 'Best Bid',
              best_ask: 'Best Ask',
              spread: 'Spread',
              ticks: '{{count}} ticks',
              top_level: 'Top Level Size',
              safe_position: 'Safe Position',
              safe_position_hint: 'up to {{value}}% slippage',
              entry_size: 'Strategy Entry',
              buy_depth: 'Buy depth',
              sell_depth: 'Sell depth',
              entry_coverage: 'Entry Coverage',
              entry_ratio: 'Liquidity / entry: {{value}}x',
              entry_unknown: 'Entry size is not set',
              entry_safe: 'Entry fits comfortably',
              entry_tight: 'Entry fits, but tightly',
              entry_risky: 'Entry is larger than visible depth',
              asks: 'Asks',
              bids: 'Bids'
            }
          },
          dashboard: {
            title: 'Dashboard',
            subtitle: 'Performance overview across your connected strategies.',
            total_pnl: 'Total PNL',
            trades_count: 'Trades Count',
            pnl_dynamics: 'PNL Dynamics',
            open_positions: 'Open Positions',
            open_positions_hint: 'Live positions reported by exchanges.',
            open_positions_active: 'Active',
            open_positions_issues: 'Issues',
            recent_trades: 'Recent Trades',
            refresh: 'Refresh',
            refreshing: 'Refreshing...',
            loading: 'Loading data...',
            error: 'Failed to load dashboard',
            ranges: { d1: '1D', d7: '7D', d30: '30D', all: 'All' },
            recent_table: {
              subtitle: 'Auto-trading feed',
              subtitle_hint: 'Trades confirmed by exchange',
              strategy: 'Strategy',
              all_strategies: 'All strategies',
              realized_pnl: 'Realized PnL',
              fees: 'Fees',
              entries: 'Entries',
              closed_at: 'Closed',
              timeframe: 'TF {{value}}m',
              share: 'Share',
              view: 'View',
              sort: 'Sort',
              sort_latest: 'Latest',
              sort_strategy: 'Strategy'
            },
            share_modal: {
              title: 'Share Trade',
              subtitle: 'Screenshot is generated on request',
              download: 'Download',
              copy: 'Copy',
              generating: 'Generating image...',
              error_generate: 'Failed to generate image.',
              error_copy: 'Failed to copy image.'
            },
          },
          analysis: {
  page_title: 'Market Pair Analysis',
  page_desc: 'Scan the market to find the best correlations and profitable setups.',
  settings_title: 'Analysis Parameters',
  main_coin: 'Primary Asset (Symbol A)',
  timeframe: 'Timeframe',
  pos_size: 'Size (Calculated)',
  comm_rate: 'Comm. Rate (%)',
  levels_plus: 'Levels (+)',
  levels_minus: 'Levels (-)',
  btn_start: 'Run Deep Analysis',
  btn_loading: 'Analyzing market...',
  wait_notice: 'Algorithm is scanning. This can take up to 3 minutes.',
  placeholder: 'Configure parameters and start scanning.',
  results_title: 'Scan Results',
  processed: 'Processed',
  error_timeout: 'Timeout reached (3 min).',
  search: 'Search',
  profit_only: 'Profit Only',
  all_results: 'All Results',
  filtered_result: 'Filtered result',
  sort_by: 'Sort by',
  sort_desc: 'Desc',
  sort_asc: 'Asc',
  no_results: 'No results found',
  error_general: 'An error occurred during analysis.',
  cards: {
    pair: 'Pair',
    equity_curve: 'Yield chart',
    trades_count: '{{count}} trades',
    avg_trade: 'Avg trade',
    best_trade: 'Max profitable trade',
    worst_trade: 'Max losing trade',
    no_losses: 'No losing trades',
    wins_losses: 'Wins / Losses',
    open_strategy: 'Open strategy',
    trade: 'Trade'
  },
  templates: {
    title: 'My Templates',
    desc: 'Save your current analysis setup and bring it back in one click.',
    auth_required: 'Sign in to save and manage analysis templates.',
    refresh: 'Refresh',
    save: 'Save Template',
    update: 'Update Template',
    apply: 'Apply',
    delete: 'Delete',
    cancel: 'Cancel',
    deleting: 'Deleting...',
    active: 'Active',
    untitled: 'Untitled template',
    empty: 'You have no saved templates yet.',
    name_placeholder: 'Template name',
    updated_at: 'Updated {{value}}',
    saved_success: 'Template "{{name}}" saved.',
    updated_success: 'Template "{{name}}" updated.',
    applied_success: 'Template "{{name}}" loaded into the form.',
    deleted_success: 'Template "{{name}}" deleted.',
    delete_title: 'Delete Template',
    delete_message: 'Delete template "{{name}}"? This action cannot be undone.',
    errors: {
      load: 'Failed to load templates.',
      save: 'Failed to save template.',
      apply: 'Failed to load template.',
      delete: 'Failed to delete template.',
      name_required: 'Enter a template name.'
    }
  },
  tooltips: { 
    main_coin: 'The base asset compared against all others.', 
    comm_rate: 'Fee % used for PnL calculation.',
    filter_corr: 'Correlation Coefficient. Indicates how strongly the assets move together. Recommended setting: > 0.8 for best results.',
    filter_win: 'Win Rate %. The percentage of trades that resulted in a profit based on historical data.',
    filter_pf: 'Profit Factor. The ratio of gross profit to gross loss. A value > 1.5 is generally considered good.'
  },
  table: { 
    symbol_b: 'Symbol B', 
    correlation: 'Correlation', 
    profit_factor: 'PF', 
    winrate: 'Winrate %', 
    wl: 'W / L', 
    pnl: 'Net PnL ($)', 
    total: 'Total Trades', 
    duration: 'Avg Duration' 
  }
},
          summary: { title: 'Strategy Summary', loading: 'Loading...', net_pnl: 'Net PnL', total_trades: 'Total Trades', winrate: 'Winrate', profit_factor: 'Profit Factor', avg_pnl: 'Avg PnL', commission: 'Total Comm.', duration: 'Avg Duration' },
          trades: {
            empty: 'No trades to display.',
            headers: {
              open: { num: 'Entry №', time: 'Time', zscore: 'Z-Score', level: 'Level', pos_a: 'Pos A', price_a: 'Price A', pos_b: 'Pos B', price_b: 'Price B' },
              completed: { num: '№', pos_a: 'А POS.', pos_b: 'В POS.', entries: 'Entries', entry_time: 'Entry Time', exit_time: 'Exit Time', duration: 'Duration', close_z: 'Close Z', pnl: 'Net PNL, $', share: 'Share' }
            },
            modal: { title: 'Trade Details', entries_title: 'Entries', exit_pnl_title: 'Exit & PNL', entry_num: 'Entry #', price: 'Price', qty: 'Qty', exit_price: 'Exit Price', pnl_coin: 'PNL', total_pnl: 'Total Net PNL' }
          },
          share_card: {
            automated_trading: 'Automated Trading',
            trade_entries: 'Trade Entries',
            entry: 'Entry #{{id}}',
            price: 'Price',
            qty: 'Qty',
            entry_amount: 'Entry',
            exit: 'Exit {{symbol}}',
            commission: 'Commission',
            rate: 'Rate',
            total_pnl: 'Total PNL',
            trades_executed_on: 'Trades executed on',
            official_partner: 'Official partner'
          },
          settings: {
            title: 'Account Settings', desc: 'Manage your profile and security.', profile_title: 'Profile Info', security_title: 'Security', login_label: 'Username', email_label: 'Email', login_hint: 'Cannot be changed.', email_hint: 'Contact support to change.',
            current_pass: 'Current Password', current_pass_placeholder: 'Enter password', new_pass: 'New Password', new_pass_placeholder: 'Min 6 chars', confirm_pass: 'Confirm New Password', confirm_pass_placeholder: 'Repeat password',
            btn_save: 'Change Password', btn_saving: 'Saving...', msg_success: 'Password changed!', msg_error_match: 'Passwords do not match.', msg_error_length: 'Min 6 characters.', msg_error_load: 'Load failed.', msg_error_change: 'Change failed.', loading: 'Loading...'
          },
          landing: {
  nav: { features: 'Features', how_it_works: 'How It Works', security: 'Security', launch: 'Launch App' },
  hero: { 
    badge: '✨ Automated Trading', 
    title_part1: 'Trade ', 
    title_accent: 'smarter', 
    title_part2: ', not harder', 
    title: 'Everything you need', 
    steps_title: 'Start trading in 3 steps', 
    subtitle: 'Connect your exchanges via API, set up your strategy, and let the platform do the work for you.', 
    start_btn: 'Start Trading' 
  },
  cta: { title: 'Ready to start trading?', btn: 'Get Access' },
  footer: { copy: '© 2025 Hedger. All rights reserved.' },
  features: { 
    api: { title: 'API Integration', desc: 'Connect your accounts from any major exchange in minutes.' }, 
    strategy: { title: 'Create Strategy', desc: 'Use our strategy builder to create your unique trading logic.' }, 
    backtest: { title: 'Backtesting', desc: 'Test your strategies on historical data and see potential returns.' }, 
    monitor: { title: 'Real-time Monitoring', desc: 'Track all your positions, balances, and trading results.' }, 
    secure: { title: 'Full Security', desc: 'Your API keys are protected with bank-level encryption.' }, 
    risk: { title: 'Risk Management', desc: 'Built-in stop-loss, take-profit, and position management tools.' } 
  },
  steps: { 
    step1: { title: 'Connect Exchange', desc: 'Get API keys from your exchange and connect in one click.' }, 
    step2: { title: 'Create Strategy', desc: 'Use our strategy builder or choose from ready-made strategies.' }, 
    step3: { title: 'Start Trading', desc: 'Test on historical data and launch automated trading.' } 
  },
  // ЭТОТ БЛОК ТАКЖЕ ДОЛЖЕН БЫТЬ В EN:
  security: {
    title: 'Security is',
    title_accent: 'our priority',
    item1: 'Bank-level AES-256 encryption',
    item2: 'API keys never stored in plain text',
    item3: 'Regular security audits'
  },
  partner: {
        title: 'Official Partner'
      }
},
          partner: {
            title: 'Partner Program',
            subtitle: 'Invite traders and track commissions. Full stats will appear after the API is connected.',
            error: 'Failed to load partner data.',
            loading: 'Loading partner data...',
            badge_soon: 'API soon',
            banner_title: 'UI placeholder',
            banner_desc: 'This page is a stub. We will connect live data once backend endpoints are ready.',
            stats: {
              total_referrals: 'Total referrals',
              active_referrals: 'Active referrals',
              total_commission: 'Total commission',
              commission_30d: 'Commission (30d)',
              avg_commission: 'Avg commission',
              avg_commission_hint: 'per referral',
              conversion_rate: 'Conversion rate',
              conversion_hint: 'signups / clicks',
              total_volume: 'Total volume',
              all_time: 'All-time',
              top_referral: 'Top referral'
            },
            invite: {
              title: 'Invite link',
              desc: 'Generate a personal link and share it with traders.',
              placeholder: 'https://hedger.bar/invite/your-code',
              code_label: 'Referral code',
              code_placeholder: 'Enter your code',
              custom_hint: 'You can use your own code',
              btn_generate: 'Generate link',
              creating: 'Creating...',
              btn_copy: 'Copy link',
              copy_success: 'Copied',
              copy_error: 'Copy failed',
              invalid_code: 'Use 3–64 symbols: a-z, 0-9, _ or -.',
              create_error: 'Failed to create link.',
              links_title: 'Your links',
              links_empty: 'No links yet.',
              links_users: '{{count}} invited',
              active: 'Active',
              inactive: 'Inactive',
              hint: 'Available after API',
              stats_title: 'Link stats',
              clicks: 'Clicks',
              signups: 'Signups',
              verified: 'Verified',
              conversion: 'Conversion',
              level1_title: 'Level 1',
              level1_desc: 'From commissions of invited traders',
              level2_title: 'Level 2',
              level2_desc: "Income from your referrals' referrals",
              latest_created: 'Last created: {{date}}',
              latest_empty: 'Generate your first link'
            },
            payouts: {
              title: 'Payout summary',
              total_paid: 'Total paid',
              pending: 'Pending',
              next_payout: 'Next payout',
              min_payout: 'Min payout',
              schedule: 'Schedule',
              schedule_monthly: 'Monthly',
              not_available: '—'
            },
            withdrawals: {
              title: 'Referral withdrawals',
              available: 'Available to withdraw',
              total_paid: 'Total paid',
              last_payout: 'Last payout',
              button: 'Withdraw',
              modal_title: 'Withdraw USDT',
              network: 'Network',
              address: 'Wallet address',
              address_placeholder: 'Enter USDT wallet address',
              amount: 'Amount',
              comment: 'Comment',
              comment_placeholder: 'Optional note',
              fee: 'Network fee',
              receive: 'You will receive',
              cancel: 'Cancel',
              confirm: 'Withdraw',
              processing: 'Processing...',
              error_amount: 'Enter a valid amount.',
              error_amount_positive: 'Amount must be greater than 0.',
              error_amount_min: 'Minimum withdrawal amount is 10 USDT.',
              error_address: 'Enter a payout address.',
              error_insufficient: 'Insufficient available balance.',
              error_network: 'Select withdrawal network.',
              error_generic: 'Failed to create withdrawal.',
              success: 'Withdrawal request created.',
              status: {
                completed: 'Completed',
                rejected: 'Rejected',
                processing: 'Processing'
              },
              table: {
                col_date: 'Date',
                col_amount: 'Amount (USDT)',
                col_network: 'Network',
                col_address: 'Address',
                col_status: 'Status',
                col_comment: 'Comment',
                empty: 'No withdrawal history yet.'
              }
            },
            tabs: {
              referrals: 'Referrals',
              links: 'Links',
              transactions: 'Transactions',
              withdrawals: 'Withdrawals'
            },
            links_table: {
              col_code: 'Code',
              col_url: 'URL',
              col_invited: 'Invited',
              col_accrual: 'Accrual',
              col_status: 'Status',
              col_created: 'Created',
              col_action: 'Action'
            },
            transactions: {
              error: 'Failed to load transactions.',
              filter_invite_code: 'Invite code',
              filter_all_codes: 'All codes',
              filter_referred_user_id: 'Referred user ID',
              filter_referred_user_placeholder: 'Optional user id',
              apply: 'Apply',
              loading_action: 'Loading...',
              col_date: 'Date',
              col_invite_code: 'Invite code',
              col_user: 'User',
              col_status: 'Status',
              col_amount: 'Amount (USDT)',
              empty: 'No transactions yet.',
              loading: 'Loading transactions...'
            },
            table: {
              title: 'Referrals',
              subtitle: 'Detailed statistics per referral',
              col_referral: 'Referral',
              col_uid: 'UID',
              col_joined: 'Joined',
              col_status: 'Status',
              col_trades: 'Trades',
              col_volume: 'Volume',
              col_commission_total: 'Commission total',
              col_commission_30d: 'Commission 30d',
              col_last_active: 'Last active',
              empty: 'No referrals yet.'
            },
            status: {
              active: 'Active',
              inactive: 'Inactive',
              new: 'New',
              paused: 'Paused'
            },
            top: {
              title: 'Top referrals',
              subtitle: 'By total commission',
              empty: 'No referrals yet.'
            },
            charts: {
              referrals_growth: 'Referral growth',
              commissions_trend: 'Commission trend',
              placeholder: 'Chart will appear here'
            },
            breakdown: {
              title: 'Commission breakdown',
              subtitle: 'By referral tier',
              tier1: 'Tier 1',
              tier2: 'Tier 2',
              tier3: 'Tier 3'
            }
          },
          manager: { add_title: 'Add to My Strategies', name_placeholder: 'Name', desc_placeholder: 'Description', save_btn: 'Add to My Strategies', saving: 'Saving...', error_no_params: 'No params.', error_no_name: 'Enter name.', error_unknown: 'Error.', my_title: 'My Strategies' }
        }
      },
      // ==========================================
      // --- RUSSIAN ---
      // ==========================================
      ru: {
        translation: {
          sidebar: {
            dashboard: 'Дашборд',
            hedger_strategies: 'Стратегии Hedger',
            my_strategies: 'Мои стратегии',
            connected_exchange: 'Подключение бирж',
            buy_usdt: 'Купить USDT',
            sell_usdt: 'Продать USDT',
            auto_trading: 'Авто-торговля',
            pair_analysis: 'Поиск стратегий',
            admin_control: 'Панель админа',
            faq: 'FAQ',
            partner_program: 'Партнерская программа',
            sign_in: 'Войти',
            sign_out: 'Выйти'
          },
          equity: {
            title: 'График доходности'
          },
          strategy_settings: {
            title: 'Настройки стратегии',
            symbol_a: 'Символ А',
            symbol_b: 'Символ Б',
            timeframe: 'Таймфрейм',
            zscore: 'Период Z-Score',
            bar_count: 'Кол-во баров',
            margin: 'Маржа (USDT)',
            leverage: 'Плечо',
            position_size: 'Размер позиции (USDT)',
            commission: 'Комиссия (%)',
            exit: 'Выход (Z-Score)',
            entry_plus: 'Уровни входа (+)',
            entry_minus: 'Уровни входа (-)',
            apply_btn: 'Применить и запустить',
            date_filter: 'Исключение дат',
            exclude_toggle: 'Включить фильтр дат',
            from: 'С',
            to: 'По',
            min_size_error: "Минимальный размер позиции 25$. Увеличьте маржу или плечо.",
            rec_balance_title: "Рекомендуемый баланс фьючерсов",
            rec_balance_desc: "Для обеспечения {{count}} уровней входа с размером позиции {{size}}$.",
            info: 'Введите уровни через запятую.',
            tooltips: {
              symbol: 'Выбираем монету которая будет торговаться.',
              timeframe: 'Таймфрейм для бектеста. Рекомендуем 5-30м.',
              zscore: 'Расстояние между отклонениями. По умолчанию 300.',
              bar_count: 'Количество свечей для бектеста. Макс 50.000.',
              margin: 'Реальная сумма USDT из вашего баланса.',
              leverage: 'Множитель для вашей маржи.',
              pos_size: 'Общий объем сделки (Маржа x Плечо) на две монеты, получается 50% суммы на каждую. Например если Вы выставили 30$, 15$ пойдут на открытие первой позиции по первой монете и 15$ по второй.',
              commission: 'Комиссия на открытие и закрытие сделок в процентах.',
              exit: 'При достижении этого отклонения позиция закроется.',
              entry_plus: 'Входы на положительных отклонениях. По умолчанию 2, 3, 4.',
              entry_minus: 'Входы на отрицательных отклонениях. По умолчанию -2, -3, -4.'
            }
          },
          stats: {
            title: 'Стратегии Hedger',
            subtitle: 'Официальные алгоритмические стратегии для автоторговли',
            by_hedger: 'От Hedger',
            risk_low: 'Низкий риск',
            risk_medium: 'Средний риск',
            risk_high: 'Высокий риск',
            open_btn: 'Открыть стратегию',
            preview_mode: 'Режим просмотра',
            default_name: 'Название стратегии',
            default_desc: 'Официальная алгоритмическая конфигурация.',
            labels: {
              z_period: 'Z-Период',
              bars: 'Бары',
              margin: 'Маржа',
              leverage: 'Плечо',
              comm: 'Комм.',
              z_exit: 'Z Выход',
              levels_plus: 'Уровни (+)',
              levels_minus: 'Уровни (-)'
            },
            errors: {
              title: 'Ошибка загрузки каталога',
              access_denied: 'Доступ запрещен: обратитесь к администратору.',
              generic: 'Не удалось загрузить стратегии.',
              empty: 'Публичные стратегии пока отсутствуют.'
            }
          },
          autotrading: {
            page_title: 'Авто-торговля',
            connect_title: 'Подключить новую стратегию',
            connected_title: 'Подключенные стратегии',
            select_account: '1. Выберите аккаунт',
            select_strategies: '2. Выберите стратегии',
            placeholder_account: 'Выберите биржевой аккаунт...',
            no_strategies: 'Нет доступных стратегий для подключения.',
            btn_connect: 'Включить авто-торговлю',
            btn_connecting: 'Подключение...',
            empty_state: 'Нет активных подключений. Используйте форму выше.',
            active_count: 'Активных стратегий',
            status: { disabled: 'Отключено', active: 'Торговля активна', stopping: 'Остановка (мягкая)' },
            controls: { start: 'Запуск', stop_graceful: 'Стоп (Мягко)', stop: 'Стоп', force_stop: 'Принудительный стоп', processing: 'Обработка...' },
            modal: { params_title: 'Параметры стратегии', unlink_title: 'Отключить стратегию', unlink_msg: 'Вы уверены, что хотите отключить стратегию "{{name}}"?', btn_cancel: 'Отмена', btn_confirm: 'Подтвердить отключение', settings: 'Настройки', unlink: 'Отключить' },
            params: { symbol_a: 'Символ А', symbol_b: 'Символ Б', timeframe: 'Таймфрейм', zscore: 'Период Z-Score', bar: 'Анализ баров', amount: 'Сумма входа', commission: 'Комиссия', short: 'Уровни Short', long: 'Уровни Long' }
          },
          accounts: {
            page_title: 'Подключение бирж',
            your_accounts: 'Ваши аккаунты',
            loading: 'Загрузка аккаунтов...',
            no_accounts: 'У вас пока нет подключенных аккаунтов.',
            add_title: 'Добавить новый аккаунт',
            select_exchange: 'Выберите биржу для подключения:',
            coming_soon: 'Скоро...',
            details_for: 'Введите данные для {{exchange}}',
            verified: 'Верифицирован',
            fields: { name: 'Название аккаунта', name_placeholder: 'например, Мой основной аккаунт', api_key: 'API ключ', api_secret: 'API секрет', api_key_new: 'Новый API ключ (опционально)', api_secret_new: 'Новый API секрет (опционально)' },
            btns: { connect: 'Подключить аккаунт', connecting: 'Подключение...', saving: 'Сохранение...', save_changes: 'Сохранить изменения', delete: 'Удалить', deleting: 'Удаление...', cancel: 'Отмена', update_keys: 'Обновить ключи', delete_account: 'Удалить аккаунт' },
            modal: { update_title: 'Обновить аккаунт: {{name}}', update_hint: 'Введите новые значения.', delete_title: 'Подтверждение удаления', delete_confirm: 'Вы уверены, что хотите удалить аккаунт {{name}}? Это действие нельзя отменить.' },
            referral_modal: {
              title: 'Проверка реферала Bybit',
              header: 'Ваш биржевой UID ({{uid}}) не найден',
              desc: 'Ваш аккаунт не связан с нашей партнерской программой. Доступ к авто-торговле открыт только для наших рефералов.',
              btn_register: 'Зарегистрировать аккаунт',
              btn_transfer: 'Инструкция по переносу',
              footer: 'После регистрации или переноса попробуйте подключить аккаунт снова.'
            },
            bybit_help: {
              title: 'Регистрация Bybit и перенос реф-кода',
              desc: 'Если у вас еще нет аккаунта Bybit, зарегистрируйтесь по нашей партнерской ссылке. Если аккаунт уже есть, воспользуйтесь инструкцией по переносу реферального кода.',
              btn_register: 'Зарегистрировать аккаунт Bybit',
              btn_transfer: 'Инструкция по переносу',
              hint: 'Без привязки аккаунта Bybit к нашей реферальной программе, у вас не будет доступа к авто-торговле.'
            },
            errors: { network: 'Произошла сетевая ошибка.', unexpected: 'Произошла непредвиденная ошибка.' }
          },
          auth: {
            common: {
              email_placeholder: 'Почта',
              password_placeholder: 'Пароль',
              show_password: 'Показать пароль',
              hide_password: 'Скрыть пароль'
            },
            login: {
              subtitle: 'С возвращением, трейдер',
              forgot_password: 'Забыли пароль?',
              sign_in: 'Войти',
              signing_in: 'Вход...',
              no_account: 'Еще нет аккаунта?',
              register_here: 'Зарегистрироваться',
              footer: 'Безопасный доступ к вашим стратегиям и аналитике',
              unexpected_error: 'Произошла непредвиденная ошибка.'
            },
            register: {
              title: 'Создать аккаунт',
              subtitle: 'Присоединяйтесь к Hedger',
              confirm_password_placeholder: 'Повторите пароль',
              have_referral: 'У меня есть реферальный код',
              referral_label: 'Реферальный код',
              referral_placeholder: 'Введите реферальный код',
              referral_hint: 'Если вы перешли по реферальной ссылке, код уже подставлен.',
              submit: 'Зарегистрироваться',
              processing: 'Обработка...',
              have_account: 'Уже есть аккаунт?',
              sign_in: 'Войти',
              footer: 'Создайте аккаунт для доступа к аналитике и стратегиям',
              unexpected_error: 'Ошибка регистрации. Попробуйте снова.',
              errors: {
                passwords_mismatch: 'Пароли не совпадают.',
                referral_required: 'Введите реферальный код.',
                token_missing: 'Не получен токен регистрации. Попробуйте снова.'
              }
            }
          },
          hub: {
            page_title: 'Мои стратегии',
            sort_by: 'Сортировать:',
            sort_name: 'Имя',
            sort_date: 'Дата',
            create_new: 'Создать стратегию',
            last_edited: 'Изменено:',
            invalid_strategy: 'Ошибка стратегии',
            corrupted_data: 'Данные стратегии повреждены.',
            untitled: 'Без названия',
            none: 'Нет',
            error_id: 'Ошибка: ID отсутствует',
            params: { pair: 'Пара', timeframe: 'Таймфрейм', zscore: 'Период Z-Score', entry_plus: 'Уровни входа (+)', entry_minus: 'Уровни входа (-)' },
            modal: { confirm_title: 'Подтверждение удаления', question: 'Удалить стратегию?', message: 'Вы уверены, что хотите удалить "{{name}}"?', btn_cancel: 'Отмена', btn_delete: 'Удалить', deleting: 'Удаление...', error_delete: 'Ошибка удаления' }
          },
          detail: {
            page_title: 'Симуляция стратегии',
            new_strategy: 'Создание новой стратегии',
            error_title: 'Ошибка загрузки стратегии',
            error_invalid_id: 'Неверный ID стратегии.',
            error_not_found: 'Стратегия не найдена.',
            back_btn: 'Назад к стратегиям',
            placeholder: 'Настройте параметры справа и нажмите "Применить и запустить".',
            open_trades: 'Открытые сделки',
            history: 'История завершенных сделок'
          },
          dashboard: {
            title: 'Дашборд',
            subtitle: 'Сводка эффективности по подключенным стратегиям.',
            total_pnl: 'Чистый PNL',
            trades_count: 'Кол-во сделок',
            pnl_dynamics: 'Динамика PNL',
            open_positions: 'Открытые позиции',
            open_positions_hint: 'Живые позиции, полученные от бирж.',
            open_positions_active: 'Активные',
            open_positions_issues: 'Проблемы',
            recent_trades: 'Последние сделки',
            refresh: 'Обновить',
            refreshing: 'Обновление...',
            loading: 'Загрузка данных...',
            error: 'Ошибка загрузки дашборда',
            ranges: { d1: '1D', d7: '7D', d30: '30D', all: 'Все' },
            recent_table: {
              subtitle: 'Лента автоторговли',
              subtitle_hint: 'Сделки подтверждены биржей',
              strategy: 'Стратегия',
              all_strategies: 'Все стратегии',
              realized_pnl: 'Реализованный PnL',
              fees: 'Комиссии',
              entries: 'Входы',
              closed_at: 'Закрытие',
              timeframe: 'ТФ {{value}}м',
              share: 'Поделиться',
              view: 'Посмотреть',
              sort: 'Сортировка',
              sort_latest: 'Сначала новые',
              sort_strategy: 'По стратегии'
            },

            share_modal: {
              title: 'Поделиться сделкой',
              subtitle: 'Скриншот генерируется по запросу',
              download: 'Скачать',
              copy: 'Скопировать',
              generating: 'Генерация изображения...',
              error_generate: 'Не удалось создать изображение.',
              error_copy: 'Не удалось скопировать изображение.'
            },
          },
          analysis: {
  page_title: 'Анализ рыночных пар',
  page_desc: 'Сканирование рынка для поиска лучших корреляций и прибыльных связок.',
  settings_title: 'Параметры анализа',
  main_coin: 'Основной актив (Symbol A)',
  timeframe: 'Таймфрейм',
  pos_size: 'Размер (Расчетный)',
  comm_rate: 'Комиссия (%)',
  levels_plus: 'Уровни (+)',
  levels_minus: 'Уровни (-)',
  btn_start: 'Запустить глубокий анализ',
  btn_loading: 'Анализ рынка...',
  wait_notice: 'Алгоритм сканирует пары. Это может занять до 3 минут.',
  placeholder: 'Настройте параметры и запустите сканирование.',
  results_title: 'Результаты сканирования',
  processed: 'Обработано',
  error_timeout: 'Превышено время ожидания (3 мин).',
  search: 'Поиск',
  profit_only: 'Только прибыльные',
  all_results: 'Все результаты',
  filtered_result: 'Отфильтровано',
  sort_by: 'Сортировка',
  sort_desc: 'По убыв.',
  sort_asc: 'По возр.',
  no_results: 'Ничего не найдено',
  error_general: 'Произошла ошибка во время анализа.',
  cards: {
    pair: 'Пара',
    equity_curve: 'График доходности',
    trades_count: '{{count}} сделок',
    avg_trade: 'Средняя сделка',
    best_trade: 'Макс. прибыльная сделка',
    worst_trade: 'Макс. убыточная сделка',
    no_losses: 'Нет убыточных сделок',
    wins_losses: 'Побед / Убытков',
    open_strategy: 'Открыть стратегию',
    trade: 'Сделка'
  },
  templates: {
    title: 'Мои шаблоны',
    desc: 'Сохраняйте текущие параметры анализа и загружайте их в один клик.',
    auth_required: 'Войдите в аккаунт, чтобы сохранять и управлять шаблонами анализа.',
    refresh: 'Обновить',
    save: 'Сохранить шаблон',
    update: 'Обновить шаблон',
    apply: 'Применить',
    delete: 'Удалить',
    cancel: 'Отмена',
    deleting: 'Удаление...',
    active: 'Активный',
    untitled: 'Шаблон без названия',
    empty: 'У вас пока нет сохраненных шаблонов.',
    name_placeholder: 'Название шаблона',
    updated_at: 'Обновлено {{value}}',
    saved_success: 'Шаблон "{{name}}" сохранен.',
    updated_success: 'Шаблон "{{name}}" обновлен.',
    applied_success: 'Шаблон "{{name}}" загружен в форму.',
    deleted_success: 'Шаблон "{{name}}" удален.',
    delete_title: 'Удалить шаблон',
    delete_message: 'Удалить шаблон "{{name}}"? Это действие нельзя отменить.',
    errors: {
      load: 'Не удалось загрузить шаблоны.',
      save: 'Не удалось сохранить шаблон.',
      apply: 'Не удалось загрузить шаблон.',
      delete: 'Не удалось удалить шаблон.',
      name_required: 'Введите название шаблона.'
    }
  },
  tooltips: { 
    main_coin: 'Базовый актив, с которым сравниваются все остальные.', 
    comm_rate: 'Процент комиссии, используемый для расчета PnL.',
    filter_corr: 'Коэффициент корреляции. Показывает, насколько синхронно движутся активы. Рекомендуется значение > 0.8 для лучших результатов.',
    filter_win: 'Винрейт %. Процент сделок, которые закрылись в плюс на основе исторических данных.',
    filter_pf: 'Профит-фактор. Отношение общей прибыли к общему убытку. Значение > 1.5 считается хорошим показателем.'
  },
  table: { 
    symbol_b: 'Symbol B', 
    correlation: 'Корреляция', 
    profit_factor: 'PF', 
    winrate: 'Винрейт %', 
    wl: 'W / L', 
    pnl: 'Чистый PnL ($)', 
    total: 'Всего сделок', 
    duration: 'Ср. длит.' 
  }
},
          summary: { title: 'Статистика', loading: 'Загрузка...', net_pnl: 'Чистая прибыль', total_trades: 'Всего сделок', winrate: 'Винрейт', profit_factor: 'ПФ', avg_pnl: 'Средний PnL', commission: 'Общая комиссия', duration: 'Ср. длит. сделки' },
          trades: {
            empty: 'Нет сделок для отображения.',
            headers: {
              open: { num: '№ входа', time: 'Время', zscore: 'Z-Score', level: 'Уровень', pos_a: 'Поз. A', price_a: 'Цена A', pos_b: 'Поз. B', price_b: 'Цена B' },
              completed: { num: '№', pos_a: 'А Поз.', pos_b: 'В Поз.', entries: 'Входы', entry_time: 'Время входа', exit_time: 'Время выхода', duration: 'Длительность', close_z: 'Z выхода', pnl: 'Чистый PNL, $', share: 'Поделиться' }
            },
            modal: { title: 'Детали сделки', entries_title: 'Входы', exit_pnl_title: 'Выход и PNL', entry_num: 'Вход №', price: 'Цена', qty: 'Кол-во', exit_price: 'Цена выхода', pnl_coin: 'PNL', total_pnl: 'Итоговый PNL' }
          },
          share_card: {
            automated_trading: 'Авто-торговля',
            trade_entries: 'Входы',
            entry: 'Вход №{{id}}',
            price: 'Цена',
            qty: 'Кол-во',
            entry_amount: 'Вход',
            exit: 'Выход {{symbol}}',
            commission: 'Комиссия',
            rate: 'Ставка',
            total_pnl: 'ИТОГО PNL',
            trades_executed_on: 'Сделки исполнены на',
            official_partner: 'Официальный партнер'
          },
          settings: {
            title: 'Настройки аккаунта', desc: 'Управление профилем.', profile_title: 'Информация профиля', security_title: 'Безопасность', login_label: 'Имя пользователя', email_label: 'Почта', login_hint: 'Нельзя изменить.', email_hint: 'Для смены свяжитесь с поддержкой.',
            current_pass: 'Текущий пароль', current_pass_placeholder: 'Введите пароль', new_pass: 'Новый пароль', new_pass_placeholder: 'Мин 6 символов', confirm_pass: 'Подтвердите пароль', confirm_pass_placeholder: 'Повторите пароль',
            btn_save: 'Изменить пароль', btn_saving: 'Сохранение...', msg_success: 'Пароль изменен!', msg_error_match: 'Пароли не совпадают.', msg_error_length: 'Минимум 6 символов.', msg_error_load: 'Ошибка загрузки.', msg_error_change: 'Ошибка смены.', loading: 'Загрузка...'
          },
          landing: {
  nav: { features: 'Возможности', how_it_works: 'Процесс', security: 'Безопасность', launch: 'Запустить' },
  hero: { 
    badge: '✨ Алгоритмическая торговля', 
    title_part1: '', 
    title_accent: 'Системный подход', 
    title_part2: ' к рынку с Hedger', 
    title: 'Всё, что вам нужно', 
    steps_title: 'Начните за 3 шага', 
    subtitle: 'Подключите биржи, настройте алгоритмы, автоматизируйте торговлю.', 
    start_btn: 'Начать торговлю' 
  },
  cta: { title: 'Готовы начать?', btn: 'Получить доступ' },
  footer: { copy: '© 2025 Hedger. Все права защищены.' },
  features: { 
    api: { title: 'Интеграция по API', desc: 'Подключите биржи за минуты.' }, 
    strategy: { title: 'Создание стратегий', desc: 'Уникальная логика.' }, 
    backtest: { title: 'Бектестинг', desc: 'Тесты на истории.' }, 
    monitor: { title: 'Мониторинг 24/7', desc: 'Отслеживайте позиции в реальном времени.' }, 
    secure: { title: 'Полная безопасность', desc: 'Шифрование AES-256.' }, 
    risk: { title: 'Риск-менеджмент', desc: 'Стоп-лоссы и лимиты.' } 
  },
  steps: { 
    step1: { title: 'Подключите биржу', desc: 'API интеграция.' }, 
    step2: { title: 'Создайте стратегию', desc: 'Свою или выберите готовую.' }, 
    step3: { title: 'Запустите', desc: 'Авто-торговлю.' } 
  },
  partner: {
        title: 'Официальный партнер'
      },
  // ВОТ ЭТОГО БЛОКА У ТЕБЯ НЕ ХВАТАЛО:
  security: {
    title: 'Безопасность —',
    title_accent: 'наш приоритет',
    item1: 'Шифрование банковского уровня AES-256',
    item2: 'Ключи никогда не хранятся в открытом виде',
    item3: 'Регулярные аудиты безопасности'
  }
          },
          partner: {
            title: 'Партнерская программа',
            subtitle: 'Приглашайте трейдеров и отслеживайте комиссию. Полные данные появятся после подключения API.',
            error: 'Не удалось загрузить данные партнера.',
            loading: 'Загрузка данных партнера...',
            badge_soon: 'API скоро',
            banner_title: 'Заглушка интерфейса',
            banner_desc: 'Это демо-страница. Подключим живые данные после готовности бэкенда.',
            stats: {
              total_referrals: 'Всего рефералов',
              active_referrals: 'Активные рефералы',
              total_commission: 'Комиссия всего',
              commission_30d: 'Комиссия за 30 дней',
              avg_commission: 'Средняя комиссия',
              avg_commission_hint: 'на реферала',
              conversion_rate: 'Конверсия',
              conversion_hint: 'регистрации / клики',
              total_volume: 'Оборот',
              all_time: 'За все время',
              top_referral: 'Топ-реферал'
            },
            invite: {
              title: 'Ссылка приглашения',
              desc: 'Создайте персональную ссылку и делитесь ею с трейдерами.',
              placeholder: 'https://hedger.bar/invite/ваш-код',
              code_label: 'Реферальный код',
              code_placeholder: 'Введите свой код',
              custom_hint: 'Можно использовать свой код',
              btn_generate: 'Создать ссылку',
              creating: 'Создание...',
              btn_copy: 'Скопировать',
              copy_success: 'Скопировано',
              copy_error: 'Не удалось скопировать',
              invalid_code: 'Используйте 3–64 символа: a-z, 0-9, _ или -.',
              create_error: 'Не удалось создать ссылку.',
              links_title: 'Ваши ссылки',
              links_empty: 'Ссылок пока нет.',
              links_users: '{{count}} приглашено',
              active: 'Активна',
              inactive: 'Неактивна',
              hint: 'Будет доступно после API',
              stats_title: 'Статистика ссылки',
              clicks: 'Клики',
              signups: 'Регистрации',
              verified: 'Верифицированы',
              conversion: 'Конверсия',
              level1_title: 'Уровень 1',
              level1_desc: 'С комиссий приглашенных трейдеров',
              level2_title: 'Уровень 2',
              level2_desc: 'Доход от рефералов ваших рефералов',
              latest_created: 'Последняя созданная: {{date}}',
              latest_empty: 'Сгенерируйте первую ссылку'
            },
            payouts: {
              title: 'Выплаты',
              total_paid: 'Выплачено',
              pending: 'Ожидается',
              next_payout: 'Следующая выплата',
              min_payout: 'Мин. выплата',
              schedule: 'График',
              schedule_monthly: 'Ежемесячно',
              not_available: '—'
            },
            withdrawals: {
              title: 'Вывод средств',
              available: 'Доступно к выводу',
              total_paid: 'Выплачено всего',
              last_payout: 'Последняя выплата',
              button: 'Вывести',
              modal_title: 'Вывод USDT',
              network: 'Сеть',
              address: 'Адрес кошелька',
              address_placeholder: 'Введите USDT адрес',
              amount: 'Сумма',
              comment: 'Комментарий',
              comment_placeholder: 'Необязательно',
              fee: 'Комиссия сети',
              receive: 'Вы получите',
              cancel: 'Отмена',
              confirm: 'Вывести',
              processing: 'Обработка...',
              error_amount: 'Введите корректную сумму.',
              error_amount_positive: 'Сумма должна быть больше 0.',
              error_amount_min: 'Минимальная сумма вывода 10 USDT.',
              error_address: 'Введите адрес для вывода.',
              error_insufficient: 'Недостаточно доступного баланса.',
              error_network: 'Выберите сеть вывода.',
              error_generic: 'Не удалось создать вывод.',
              success: 'Заявка на вывод создана.',
              status: {
                completed: 'Завершен',
                rejected: 'Отклонен',
                processing: 'В обработке'
              },
              table: {
                col_date: 'Дата',
                col_amount: 'Сумма (USDT)',
                col_network: 'Сеть',
                col_address: 'Адрес',
                col_status: 'Статус',
                col_comment: 'Комментарий',
                empty: 'История выводов пуста.'
              }
            },
            tabs: {
              referrals: 'Рефералы',
              links: 'Ссылки',
              transactions: 'Транзакции',
              withdrawals: 'Выводы'
            },
            links_table: {
              col_code: 'Код',
              col_url: 'URL',
              col_invited: 'Приглашено',
              col_accrual: 'Начислено',
              col_status: 'Статус',
              col_created: 'Создана',
              col_action: 'Действие'
            },
            transactions: {
              error: 'Не удалось загрузить транзакции.',
              filter_invite_code: 'Инвайт-код',
              filter_all_codes: 'Все коды',
              filter_referred_user_id: 'ID реферала',
              filter_referred_user_placeholder: 'Необязательный ID пользователя',
              apply: 'Применить',
              loading_action: 'Загрузка...',
              col_date: 'Дата',
              col_invite_code: 'Инвайт-код',
              col_user: 'Пользователь',
              col_status: 'Статус',
              col_amount: 'Сумма (USDT)',
              empty: 'Транзакций пока нет.',
              loading: 'Загрузка транзакций...'
            },
            table: {
              title: 'Рефералы',
              subtitle: 'Подробная статистика по каждому рефералу',
              col_referral: 'Реферал',
              col_uid: 'UID',
              col_joined: 'Дата регистрации',
              col_status: 'Статус',
              col_trades: 'Сделок',
              col_volume: 'Объем',
              col_commission_total: 'Комиссия всего',
              col_commission_30d: 'Комиссия 30д',
              col_last_active: 'Последняя активность',
              empty: 'Рефералов пока нет.'
            },
            status: {
              active: 'Активен',
              inactive: 'Неактивен',
              new: 'Новый',
              paused: 'Пауза'
            },
            top: {
              title: 'Топ-рефералы',
              subtitle: 'По сумме комиссии',
              empty: 'Рефералов пока нет.'
            },
            charts: {
              referrals_growth: 'Рост рефералов',
              commissions_trend: 'Динамика комиссии',
              placeholder: 'График появится здесь'
            },
            breakdown: {
              title: 'Разбивка комиссий',
              subtitle: 'По уровням',
              tier1: 'Уровень 1',
              tier2: 'Уровень 2',
              tier3: 'Уровень 3'
            }
          },
          manager: { add_title: 'Сохранить стратегию', name_placeholder: 'Название', desc_placeholder: 'Описание', save_btn: 'Добавить в мои стратегии', saving: 'Сохранение...', error_no_params: 'Нет данных.', error_no_name: 'Введите имя.', error_unknown: 'Ошибка.', my_title: 'Мои стратегии' }
        }
      }
    }
  });

export default i18n;
